import React, { useMemo, useState } from 'react';
import {
    Building,
    Trophy,
    TriangleAlert,
    Users,
    UsersRound,
    X
} from 'lucide-react';
import {
    ChallengePageEntrySupportDto,
    CommunityPageEntrySupportDto,
    TeamSupportDto
} from '@api/ChallengeClient';
import { TenantPageEntrySupportDto } from '@src/core/api/TenantClient';
import { LocalizedTextDisplay } from '@components/localized-text-display';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Checkbox } from '@components/ui/checkbox';
import { Switch } from '@components/ui/switch';
import { injectTSDI } from '@core/tsdi';
import { Api } from '@core/api';
import { buildSmartConnectValidationContext } from '@src/onboarding/smart-connect/smart-connect-validation';

type SelectionPreviewProps = {
    selectedTenants: TenantPageEntrySupportDto[];
    selectedCommunities: CommunityPageEntrySupportDto[];
    selectedTeams: TeamSupportDto[];
    selectedChallenges: ChallengePageEntrySupportDto[];
    communityTeamRequired: string[];
    setSelectedTenants: (tenants: TenantPageEntrySupportDto[]) => void;
    setSelectedCommunities: (
        communities: CommunityPageEntrySupportDto[]
    ) => void;
    setSelectedTeams: (teams: TeamSupportDto[]) => void;
    setSelectedChallenges: (challenges: ChallengePageEntrySupportDto[]) => void;
    setCommunityTeamRequired: (communityInvitationCodes: string[]) => void;
    whitelabelCommunityId: number | null;
    setWhitelabelCommunityId: (communityId: number | null) => void;
};

export const SelectionPreview: React.FC<SelectionPreviewProps> = ({
    selectedTenants,
    selectedCommunities,
    selectedTeams,
    selectedChallenges,
    communityTeamRequired,
    setSelectedTenants,
    setSelectedCommunities,
    setSelectedTeams,
    setSelectedChallenges,
    setCommunityTeamRequired,
    whitelabelCommunityId,
    setWhitelabelCommunityId
}) => {
    const { challengeClient } = injectTSDI(Api);
    const [generatingCodes, setGeneratingCodes] = useState<Set<string>>(
        new Set()
    );
    const validationContext = useMemo(
        () =>
            buildSmartConnectValidationContext({
                selectedTenants,
                selectedCommunities,
                selectedTeams,
                selectedChallenges,
                communityTeamRequired
            }),
        [
            selectedTenants,
            selectedCommunities,
            selectedTeams,
            selectedChallenges,
            communityTeamRequired
        ]
    );

    const hasSelectedItems =
        selectedTenants.length > 0 ||
        selectedCommunities.length > 0 ||
        selectedTeams.length > 0 ||
        selectedChallenges.length > 0;

    if (!hasSelectedItems) {
        return (
            <div className="flex flex-col items-center justify-center gap-1 py-8 text-center text-muted-foreground">
                <p className="text-base font-medium">No items selected</p>
                <p className="text-sm">
                    Select tenants, communities, teams, or challenges to
                    generate a SmartConnect link
                </p>
            </div>
        );
    }

    const renderItemCard = (
        icon: React.ReactNode,
        title: string | React.ReactNode,
        subtitle?: string,
        context?: { label: string; value: string }[],
        hasIssues = false,
        onDelete?: () => void,
        footer?: React.ReactNode
    ) => (
        <div className="relative h-full rounded-lg border bg-card p-3 transition-colors hover:border-primary">
            {onDelete && (
                <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Remove"
                    onClick={onDelete}
                    className="absolute top-1 right-1 text-muted-foreground hover:text-destructive"
                >
                    <X />
                </Button>
            )}
            <div className="flex items-start gap-2 pr-6">
                <span className="mt-0.5 text-primary [&_svg]:size-5">
                    {icon}
                </span>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1">
                        <div className="flex-1 text-sm font-semibold">
                            {title}
                        </div>
                        {hasIssues && (
                            <TriangleAlert className="size-4 text-warning" />
                        )}
                    </div>
                    {subtitle && (
                        <span className="mt-0.5 block text-xs text-muted-foreground">
                            {subtitle}
                        </span>
                    )}
                    {context && context.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                            {context.map((ctx, idx) => (
                                <Badge key={idx} variant="outline">
                                    {ctx.label}: {ctx.value}
                                </Badge>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            {footer && <div className="mt-3 border-t pt-3">{footer}</div>}
        </div>
    );

    const getTenantInfo = (item: any): string | undefined => {
        return item?.tenant?.name || item?.tenantId;
    };

    const getCommunityInfo = (
        item: any
    ): { name: string; type?: string } | undefined => {
        if (!item?.community) {
            return undefined;
        }
        return {
            name: item.community.name,
            type: item.community.type
        };
    };

    const handleGenerateTeamCode = async (teamId: number) => {
        const key = `team-${teamId}`;
        setGeneratingCodes((prev) => new Set(prev).add(key));

        try {
            const response =
                await challengeClient.support.teamSupportControllerGenerateInvitationCode(
                    teamId
                );
            const invitationCode = response.data.invitationCode;

            setSelectedTeams(
                selectedTeams.map((team) =>
                    team.id === teamId ? { ...team, invitationCode } : team
                )
            );
        } catch (error) {
            console.error('Error generating team invitation code:', error);
        } finally {
            setGeneratingCodes((prev) => {
                const newSet = new Set(prev);
                newSet.delete(key);
                return newSet;
            });
        }
    };

    const handleGenerateChallengeCode = async (challengeId: number) => {
        const key = `challenge-${challengeId}`;
        setGeneratingCodes((prev) => new Set(prev).add(key));

        try {
            const response =
                await challengeClient.support.challengeSupportControllerGenerateInvitationCode(
                    challengeId
                );
            const invitationCode = response.data.invitationCode;

            setSelectedChallenges(
                selectedChallenges.map((challenge) =>
                    challenge.id === challengeId
                        ? { ...challenge, invitationCode }
                        : challenge
                )
            );
        } catch (error) {
            console.error('Error generating challenge invitation code:', error);
        } finally {
            setGeneratingCodes((prev) => {
                const newSet = new Set(prev);
                newSet.delete(key);
                return newSet;
            });
        }
    };

    type ValidationIssue = {
        message: string;
        blocking?: boolean;
        action?: {
            label: string;
            handler: () => void;
            loading: boolean;
        };
    };

    const validateSelection = (): ValidationIssue[] => {
        const issues: ValidationIssue[] = [];
        const selectedTenantIds = new Set(selectedTenants.map((t) => t.name));
        const selectedCommunityIds = new Set(
            selectedCommunities.map((c) => c.id)
        );

        selectedTenants.forEach((tenant) => {
            if (!tenant.invitationCode) {
                issues.push({
                    message: `Tenant "${tenant.name}" is missing an invitation code`
                });
            }
        });

        selectedCommunities.forEach((community) => {
            if (!community.invitationCode) {
                issues.push({
                    message: `Community "${community.name}" is missing an invitation code`
                });
            }

            const tenantId = getTenantInfo(community);
            if (tenantId && !selectedTenantIds.has(tenantId)) {
                issues.push({
                    message: `Community "${community.name}" has tenant "${tenantId}" but this tenant is not selected`
                });
            }
        });

        selectedTeams.forEach((team) => {
            if (!team.invitationCode) {
                const isGenerating = generatingCodes.has(`team-${team.id}`);
                issues.push({
                    message: `Team "${team.name}" is missing an invitation code`,
                    action: {
                        label: 'Generate invite code',
                        handler: () => handleGenerateTeamCode(team.id),
                        loading: isGenerating
                    }
                });
            }

            const communityInfo = getCommunityInfo(team);
            if (
                communityInfo &&
                team.community?.id &&
                !selectedCommunityIds.has(team.community.id)
            ) {
                issues.push({
                    message: `Team "${team.name}" belongs to community "${communityInfo.name}" but this community is not selected`
                });
            }
        });

        selectedChallenges.forEach((challenge) => {
            if (!challenge.invitationCode) {
                const isGenerating = generatingCodes.has(
                    `challenge-${challenge.id}`
                );
                issues.push({
                    message: `Challenge "${
                        challenge.presentation.name.en || 'Unknown'
                    }" is missing an invitation code`,
                    action: {
                        label: 'Generate invite code',
                        handler: () =>
                            handleGenerateChallengeCode(challenge.id),
                        loading: isGenerating
                    }
                });
            }

            const communityInfo = getCommunityInfo(challenge);
            if (
                communityInfo &&
                challenge.community?.id &&
                !selectedCommunityIds.has(challenge.community.id)
            ) {
                issues.push({
                    message: `Challenge "${
                        challenge.presentation.name.en || 'Unknown'
                    }" belongs to community "${
                        communityInfo.name
                    }" but this community is not selected`
                });
            }
        });

        validationContext.blockingIssues.forEach((issue) => {
            issues.push({
                message: issue.message,
                blocking: issue.blocking
            });
        });

        return issues;
    };

    const validationIssues = validateSelection();

    const itemHasIssues = (itemName: string): boolean => {
        return validationIssues.some((issue) =>
            issue.message.includes(`"${itemName}"`)
        );
    };

    const toggleCommunityTeamRequired = (
        community: CommunityPageEntrySupportDto,
        enabled: boolean
    ) => {
        if (!community.invitationCode) {
            return;
        }

        if (enabled) {
            setCommunityTeamRequired([
                ...validationContext.communityTeamRequired,
                community.invitationCode
            ]);
            return;
        }

        setCommunityTeamRequired(
            validationContext.communityTeamRequired.filter(
                (code) => code !== community.invitationCode
            )
        );
    };

    return (
        <div className="mb-6">
            <p className="mb-4 text-sm font-medium text-muted-foreground">
                Selected Items (
                {selectedTenants.length +
                    selectedCommunities.length +
                    selectedTeams.length +
                    selectedChallenges.length}
                )
            </p>

            <div className="grid gap-4 md:grid-cols-2">
                {selectedTenants.map((tenant) => (
                    <React.Fragment key={`tenant-${tenant.name}`}>
                        {renderItemCard(
                            <Building />,
                            tenant.name,
                            tenant.type,
                            undefined,
                            itemHasIssues(tenant.name),
                            () =>
                                setSelectedTenants(
                                    selectedTenants.filter(
                                        (t) => t.name !== tenant.name
                                    )
                                )
                        )}
                    </React.Fragment>
                ))}

                {selectedCommunities.map((community) => {
                    const tenantName = getTenantInfo(community);
                    const context = tenantName
                        ? [{ label: 'Tenant', value: tenantName }]
                        : [];
                    const requiresTeamChoice =
                        !!community.invitationCode &&
                        validationContext.communityTeamRequired.includes(
                            community.invitationCode
                        );

                    return (
                        <React.Fragment
                            key={`community-${community.id || community.name}`}
                        >
                            {renderItemCard(
                                <Users />,
                                community.name,
                                community.type,
                                context,
                                itemHasIssues(community.name),
                                () => {
                                    setSelectedCommunities(
                                        selectedCommunities.filter(
                                            (c) => c.id !== community.id
                                        )
                                    );
                                    if (
                                        community.id === whitelabelCommunityId
                                    ) {
                                        setWhitelabelCommunityId(null);
                                    }
                                    if (community.invitationCode) {
                                        setCommunityTeamRequired(
                                            validationContext.communityTeamRequired.filter(
                                                (code) =>
                                                    code !==
                                                    community.invitationCode
                                            )
                                        );
                                    }
                                },
                                <div className="flex flex-col gap-3">
                                    <label className="flex cursor-pointer items-start gap-2">
                                        <Switch
                                            checked={requiresTeamChoice}
                                            disabled={!community.invitationCode}
                                            onCheckedChange={(checked) =>
                                                toggleCommunityTeamRequired(
                                                    community,
                                                    checked
                                                )
                                            }
                                        />
                                        <span className="flex flex-col">
                                            <span className="text-sm font-medium">
                                                Require team choice
                                            </span>
                                            <span className="text-xs text-muted-foreground">
                                                QR-only. User must pick a team
                                                after joining this community.
                                            </span>
                                            {!community.invitationCode && (
                                                <span className="text-xs text-warning">
                                                    Requires a community
                                                    invitation code
                                                </span>
                                            )}
                                        </span>
                                    </label>
                                    {community.whitelabelId &&
                                        community.whitelabelEnabled !==
                                            false && (
                                            <label className="flex cursor-pointer items-start gap-2">
                                                <Checkbox
                                                    checked={
                                                        community.id ===
                                                        whitelabelCommunityId
                                                    }
                                                    onCheckedChange={(
                                                        checked
                                                    ) =>
                                                        setWhitelabelCommunityId(
                                                            checked === true
                                                                ? community.id
                                                                : null
                                                        )
                                                    }
                                                />
                                                <span className="flex flex-col">
                                                    <span className="text-sm font-medium">
                                                        Enable whitelabel
                                                    </span>
                                                    <span className="text-xs text-muted-foreground">
                                                        Switches on the
                                                        community whitelabel (
                                                        {community.whitelabelId}
                                                        ) for people who join
                                                        through this link.
                                                    </span>
                                                </span>
                                            </label>
                                        )}
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}

                {selectedTeams.map((team) => {
                    const communityInfo = getCommunityInfo(team);
                    const tenantName = getTenantInfo(team);
                    const context = [];

                    if (communityInfo) {
                        const communityLabel = communityInfo.type
                            ? `${communityInfo.name} (${communityInfo.type})`
                            : communityInfo.name;
                        context.push({
                            label: 'Community',
                            value: communityLabel
                        });
                    }
                    if (tenantName) {
                        context.push({ label: 'Tenant', value: tenantName });
                    }

                    return (
                        <React.Fragment key={`team-${team.id || team.name}`}>
                            {renderItemCard(
                                <UsersRound />,
                                team.name,
                                undefined,
                                context,
                                itemHasIssues(team.name),
                                () =>
                                    setSelectedTeams(
                                        selectedTeams.filter(
                                            (t) => t.id !== team.id
                                        )
                                    )
                            )}
                        </React.Fragment>
                    );
                })}

                {selectedChallenges.map((challenge) => {
                    const communityInfo = getCommunityInfo(challenge);
                    const tenantName = getTenantInfo(challenge);
                    const context = [];

                    if (communityInfo) {
                        const communityLabel = communityInfo.type
                            ? `${communityInfo.name} (${communityInfo.type})`
                            : communityInfo.name;
                        context.push({
                            label: 'Community',
                            value: communityLabel
                        });
                    }
                    if (tenantName) {
                        context.push({ label: 'Tenant', value: tenantName });
                    }

                    const challengeName =
                        challenge.presentation.name.en || 'Unknown';

                    return (
                        <React.Fragment key={`challenge-${challenge.id}`}>
                            {renderItemCard(
                                <Trophy />,
                                <LocalizedTextDisplay
                                    value={challenge.presentation.name}
                                    variant="body2"
                                />,
                                challenge.status,
                                context,
                                itemHasIssues(challengeName),
                                () =>
                                    setSelectedChallenges(
                                        selectedChallenges.filter(
                                            (c) => c.id !== challenge.id
                                        )
                                    )
                            )}
                        </React.Fragment>
                    );
                })}
            </div>

            {validationIssues.length > 0 && (
                <div className="mt-6">
                    <p className="mb-3 flex items-center gap-1 text-sm font-semibold">
                        <TriangleAlert className="size-4 text-warning" />
                        Validation Issues ({
                            validationIssues.length
                        })
                    </p>
                    <div className="flex flex-col gap-2">
                        {validationIssues.map((issue, index) => (
                            <Alert
                                key={index}
                                variant={
                                    issue.blocking ? 'destructive' : 'warning'
                                }
                            >
                                <AlertDescription className="flex w-full items-center justify-between gap-2">
                                    <span>{issue.message}</span>
                                    {issue.action && (
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="whitespace-nowrap"
                                            onClick={issue.action.handler}
                                            disabled={issue.action.loading}
                                        >
                                            {issue.action.loading
                                                ? 'Generating...'
                                                : issue.action.label}
                                        </Button>
                                    )}
                                </AlertDescription>
                            </Alert>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
